import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Package, Plus, Search, Filter, Edit, Trash2, Eye, Download,
  Box, Cpu, Router, Battery, Car, Smartphone, Wrench, Settings,
  RefreshCw, ChevronDown, X, Check, AlertCircle, CreditCard, MapPin, TrendingDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const categoryIcons = {
  olt: Cpu,
  router: Router,
  ups: Battery,
  komputer: Cpu,
  mobil: Car,
  motor: Car,
  peralatan_kerja: Wrench,
  opm: Settings,
  otdr: Settings,
  hp: Smartphone,
  kabel: Box,
  splitter: Box,
  ont: Box,
  furniture: Box,
  lainnya: Package
};

// Condition colors
const conditionColors = {
  baik: 'bg-green-100 text-green-700',
  rusak_ringan: 'bg-yellow-100 text-yellow-700',
  rusak_berat: 'bg-red-100 text-red-700',
  dalam_perbaikan: 'bg-blue-100 text-blue-700',
  tidak_aktif: 'bg-gray-100 text-gray-700'
};

// Status colors
const statusColors = {
  aktif: 'bg-green-500 text-white',
  tidak_aktif: 'bg-gray-500 text-white',
  dijual: 'bg-orange-500 text-white',
  dihapus: 'bg-red-500 text-white'
};

const Inventory = () => {
  const { token, user: currentUser } = useAuth();
  const [assets, setAssets] = useState([]);
  const [branches, setBranches] = useState([]);
  // Role-based access control
  const userRole = (currentUser?.role || '').toLowerCase();
  const isKepalaCabang = userRole === 'kepala_cabang';
  const userBranchId = currentUser?.branch_id ? String(currentUser.branch_id) : '';
  const canWriteInventory = ['super_admin', 'superadmin', 'admin', 'owner', 'tenant_admin', 'kepala_cabang'].includes(userRole);
  // Branches yang ditampilkan di dropdown (kepala_cabang hanya cabang sendiri)
  const visibleBranches = isKepalaCabang ? branches.filter(b => String(b.id) === userBranchId) : branches;

  const [categories, setCategories] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  
  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ show: false, asset: null });
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Toast helper function
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };
  
  // Form state
  const [form, setForm] = useState({
    code: '',
    name: '',
    category: 'lainnya',
    description: '',
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_price: 0,
    supplier: '',
    invoice_number: '',
    location: '',
    assigned_to: '',
    branch_id: '',
    condition: 'baik',
    status: 'aktif',
    brand: '',
    model: '',
    serial_number: '',
    useful_life_years: 4,
    salvage_value: 0,
    warranty_end_date: '',
    notes: '',
    auto_journal: true
  });

  useEffect(() => {
    if (token) {
      fetchData();
    }
    // eslint-disable-line
  }, [token, filterBranch]);

  // Auto-lock branch untuk kepala_cabang
  useEffect(() => {
    if (isKepalaCabang && userBranchId) {
      setFilterBranch(userBranchId);
      setForm(prev => ({ ...prev, branch_id: userBranchId }));
    }
  }, [isKepalaCabang, userBranchId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const bp = filterBranch ? `?branch_id=${encodeURIComponent(filterBranch)}` : '';
      const [assetsRes, catRes, condRes, statRes, summaryRes, branchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/inventory/assets${bp}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/inventory/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/inventory/conditions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/inventory/statuses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/inventory/summary${bp}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/branches`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { data: [] } }))
      ]);
      
      if (assetsRes.data.success) setAssets(assetsRes.data.data);
      if (catRes.data.success) setCategories(catRes.data.data);
      if (condRes.data.success) setConditions(condRes.data.data);
      if (statRes.data.success) setStatuses(statRes.data.data);
      if (summaryRes.data.success) setSummary(summaryRes.data.data);
      if (branchesRes?.data?.data) setBranches(Array.isArray(branchesRes.data.data) ? branchesRes.data.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAsset = async () => {
    if (!form.name) {
      showToast('Nama aset harus diisi!', 'error');
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_URL}/api/inventory/assets`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showToast(response.data.message, 'success');
        setShowAddModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error adding asset:', error);
      showToast('Gagal menambahkan aset: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAsset = async () => {
    if (!selectedAsset) return;
    
    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/api/inventory/assets/${selectedAsset.id}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showToast(response.data.message, 'success');
        setShowDetailModal(false);
        setSelectedAsset(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error updating asset:', error);
      showToast('Gagal memperbarui aset: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAsset = async (asset) => {
    setConfirmDialog({ show: true, asset });
  };
  
  const confirmDeleteAsset = async () => {
    const asset = confirmDialog.asset;
    if (!asset) return;
    
    try {
      const response = await axios.delete(
        `${API_URL}/api/inventory/assets/${asset.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showToast(response.data.message, 'success');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      showToast('Gagal menghapus aset: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setConfirmDialog({ show: false, asset: null });
    }
  };

  const handleViewAsset = async (asset) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/inventory/assets/${asset.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSelectedAsset(response.data.data);
        setForm({
          name: response.data.data.name || '',
          category: response.data.data.category || 'lainnya',
          description: response.data.data.description || '',
          location: response.data.data.location || '',
          assigned_to: response.data.data.assigned_to || '',
          branch_id: response.data.data.branch_id || '',
          condition: response.data.data.condition || 'baik',
          status: response.data.data.status || 'aktif',
          brand: response.data.data.brand || '',
          model: response.data.data.model || '',
          serial_number: response.data.data.serial_number || '',
          useful_life_years: response.data.data.useful_life_years || 4,
          salvage_value: response.data.data.salvage_value || 0,
          warranty_end_date: response.data.data.warranty_end_date?.split('T')[0] || '',
          notes: response.data.data.notes || ''
        });
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching asset:', error);
    }
  };

  const handleEditAsset = async (asset) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/inventory/assets/${asset.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const data = response.data.data;
        setSelectedAsset(data);
        setForm({
          name: data.name || '',
          category: data.category || 'lainnya',
          description: data.description || '',
          purchase_date: data.purchase_date?.split('T')[0] || '',
          purchase_price: data.purchase_price || 0,
          supplier: data.supplier || '',
          invoice_number: data.invoice_number || '',
          location: data.location || '',
          assigned_to: data.assigned_to || '',
          branch_id: data.branch_id || '',
          condition: data.condition || 'baik',
          status: data.status || 'aktif',
          brand: data.brand || '',
          model: data.model || '',
          serial_number: data.serial_number || '',
          useful_life_years: data.useful_life_years || 4,
          salvage_value: data.salvage_value || 0,
          warranty_end_date: data.warranty_end_date?.split('T')[0] || '',
          notes: data.notes || ''
        });
        setShowEditModal(true);
      }
    } catch (error) {
      console.error('Error fetching asset:', error);
    }
  };

  const handlePrintAll = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Daftar Inventaris</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
          h1 { text-align: center; font-size: 16px; margin-bottom: 5px; }
          .date { text-align: center; font-size: 10px; color: #666; margin-bottom: 15px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #333; padding: 4px 6px; text-align: left; }
          th { background: #f0f0f0; font-size: 10px; }
          td { font-size: 10px; }
          .right { text-align: right; }
          .center { text-align: center; }
          .summary { margin-top: 15px; font-size: 11px; }
          .summary td { border: none; padding: 2px 6px; }
          .total { font-weight: bold; }
          @media print {
            body { margin: 0; }
            @page { margin: 10mm; size: landscape; }
          }
        </style>
      </head>
      <body>
        <h1>DAFTAR INVENTARIS ASET</h1>
        <p class="date">Dicetak: ${new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Kode</th>
              <th>Nama Aset</th>
              <th>Kategori</th>
              <th>Merk/Model</th>
              <th>No. Seri</th>
              <th>Tgl Beli</th>
              <th>Lokasi</th>
              <th>Kondisi</th>
              <th class="right">Harga Beli</th>
              <th class="right">Akum. Peny.</th>
              <th class="right">Nilai Buku</th>
            </tr>
          </thead>
          <tbody>
            ${filteredAssets.map((asset, idx) => `
              <tr>
                <td class="center">${idx + 1}</td>
                <td>${asset.code}</td>
                <td>${asset.name}</td>
                <td>${asset.category_label}</td>
                <td>${asset.brand || ''} ${asset.model || ''}</td>
                <td>${asset.serial_number || '-'}</td>
                <td>${asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString('id-ID') : '-'}</td>
                <td>${asset.location || '-'}</td>
                <td>${asset.condition_label}</td>
                <td class="right">${formatCurrency(asset.purchase_price)}</td>
                <td class="right">${formatCurrency(asset.accumulated_depreciation)}</td>
                <td class="right">${formatCurrency(asset.book_value)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <table class="summary">
          <tr>
            <td width="70%"></td>
            <td class="total">Total Aset:</td>
            <td class="total right">${filteredAssets.length} unit</td>
          </tr>
          <tr>
            <td></td>
            <td class="total">Total Nilai Perolehan:</td>
            <td class="total right">${formatCurrency(filteredAssets.reduce((sum, a) => sum + (a.purchase_price || 0), 0))}</td>
          </tr>
          <tr>
            <td></td>
            <td class="total">Total Akum. Penyusutan:</td>
            <td class="total right">${formatCurrency(filteredAssets.reduce((sum, a) => sum + (a.accumulated_depreciation || 0), 0))}</td>
          </tr>
          <tr>
            <td></td>
            <td class="total">Total Nilai Buku:</td>
            <td class="total right">${formatCurrency(filteredAssets.reduce((sum, a) => sum + (a.book_value || 0), 0))}</td>
          </tr>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const resetForm = () => {
    setForm({
      code: '',
      name: '',
      category: 'lainnya',
      description: '',
      purchase_date: new Date().toISOString().split('T')[0],
      purchase_price: 0,
      supplier: '',
      invoice_number: '',
      location: '',
      assigned_to: '',
      branch_id: '',
      condition: 'baik',
      status: 'aktif',
      brand: '',
      model: '',
      serial_number: '',
      useful_life_years: 4,
      salvage_value: 0,
      warranty_end_date: '',
      notes: '',
      auto_journal: true
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    const matchSearch = !searchTerm || 
      asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.serial_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = !filterCategory || asset.category === filterCategory;
    const matchStatus = !filterStatus || asset.status === filterStatus;
    const matchBranch = !filterBranch || String(asset.branch_id || '') === String(filterBranch);
    return matchSearch && matchCategory && matchStatus && matchBranch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
        <p className="text-sm text-gray-500 dark:text-slate-400">Memuat data...</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-[100] animate-in slide-in-from-top-2 fade-in duration-300">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${
            toast.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className={`p-1 rounded-full ${toast.type === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
              {toast.type === 'success' ? (
                <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
              ) : (
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              )}
            </div>
            <span className="text-sm font-medium">{toast.message}</span>
            <button 
              onClick={() => setToast({ ...toast, show: false })}
              className="ml-2 p-1 hover:bg-black/5 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500 rounded-lg">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Inventaris</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400">Kelola aset dan inventaris perusahaan</p>
          </div>
        </div>
        {canWriteInventory && (
        <Button 
          onClick={() => { resetForm(); setShowAddModal(true); }}
          data-testid="add-asset-btn"
          className="gap-1.5 h-8 text-xs bg-purple-500 hover:bg-purple-600"
        >
          <Plus className="w-3.5 h-3.5" /> Tambah Aset
        </Button>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200">
            <p className="text-[10px] text-purple-600 uppercase dark:text-purple-400">Total Aset</p>
            <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{summary.total_assets}</p>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
            <p className="text-[10px] text-blue-600 uppercase dark:text-blue-400">Nilai Perolehan</p>
            <p className="text-sm font-bold text-blue-700 dark:text-blue-300">{formatCurrency(summary.total_value)}</p>
          </div>
          <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200">
            <p className="text-[10px] text-orange-600 uppercase dark:text-orange-400">Akum. Penyusutan</p>
            <p className="text-sm font-bold text-orange-700 dark:text-orange-300">{formatCurrency(summary.total_depreciation)}</p>
          </div>
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200">
            <p className="text-[10px] text-green-600 uppercase dark:text-green-400">Nilai Buku</p>
            <p className="text-sm font-bold text-green-700 dark:text-green-300">{formatCurrency(summary.total_book_value)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Cari aset..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-8 h-8 text-xs" 
          />
        </div>
        <select 
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="h-8 text-xs border rounded px-2 bg-white dark:bg-gray-800"
        >
          <option value="">Semua Kategori</option>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select 
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="h-8 text-xs border rounded px-2 bg-white dark:bg-gray-800"
        >
          <option value="">Semua Status</option>
          {statuses.map(stat => (
            <option key={stat.value} value={stat.value}>{stat.label}</option>
          ))}
        </select>
        <select 
          value={filterBranch}
          onChange={(e) => setFilterBranch(e.target.value)}
          disabled={isKepalaCabang}
          data-testid="filter-branch-select"
          className="h-8 text-xs border rounded px-2 bg-white dark:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <option value="">{isKepalaCabang ? (currentUser?.branch_name || 'Cabang Anda') : 'Semua Cabang'}</option>
          {visibleBranches.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={fetchData} className="h-8 gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={handlePrintAll} className="h-8 gap-1">
          <Download className="w-3 h-3" /> Print
        </Button>
      </div>

      {/* Assets Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-medium">Kode</th>
              <th className="text-left px-3 py-2 text-xs font-medium">Nama Aset</th>
              <th className="text-left px-3 py-2 text-xs font-medium hidden md:table-cell">Kategori</th>
              <th className="text-left px-3 py-2 text-xs font-medium hidden lg:table-cell">Tgl Beli</th>
              <th className="text-left px-3 py-2 text-xs font-medium hidden xl:table-cell">Lokasi</th>
              <th className="text-left px-3 py-2 text-xs font-medium hidden sm:table-cell">Kondisi</th>
              <th className="text-right px-3 py-2 text-xs font-medium">Nilai Buku</th>
              <th className="text-center px-3 py-2 text-xs font-medium w-24">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredAssets.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-gray-500 text-xs dark:text-slate-400">
                  {searchTerm || filterCategory || filterStatus ? 'Tidak ada aset yang cocok' : 'Belum ada aset'}
                </td>
              </tr>
            ) : (
              filteredAssets.map((asset) => {
                const Icon = categoryIcons[asset.category] || Package;
                return (
                  <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2 font-mono text-[10px]">{asset.code}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-purple-500" />
                        <div>
                          <p className="text-xs font-medium">{asset.name}</p>
                          {asset.brand && <p className="text-[10px] text-gray-500 dark:text-slate-400">{asset.brand} {asset.model}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">{asset.category_label}</td>
                    <td className="px-3 py-2 text-xs hidden lg:table-cell">
                      {asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString('id-ID') : '-'}
                    </td>
                    <td className="px-3 py-2 text-xs hidden xl:table-cell">
                      {asset.branch_name ? (
                        <div>
                          <p className="font-medium text-purple-600 dark:text-purple-400">{asset.branch_name}</p>
                          {asset.location && <p className="text-[10px] text-gray-500 dark:text-slate-400">{asset.location}</p>}
                        </div>
                      ) : (asset.location || '-')}
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${conditionColors[asset.condition] || 'bg-gray-100'}`}>
                        {asset.condition_label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(asset.book_value)}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-center gap-1">
                        <button 
                          onClick={() => handleViewAsset(asset)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:bg-blue-900/30"
                          title="Lihat Detail"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          onClick={() => handleEditAsset(asset)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded dark:text-green-400 dark:hover:bg-green-900/30"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          onClick={() => handleDeleteAsset(asset)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded dark:text-red-400 dark:hover:bg-red-900/30"
                          title="Hapus"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add Asset Modal - Professional Design */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-5 py-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">Tambah Aset Baru</h3>
                  <p className="text-purple-100 text-xs">Isi data inventaris perusahaan</p>
                </div>
              </div>
              <button 
                onClick={() => setShowAddModal(false)} 
                className="text-white/70 hover:text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-5 overflow-y-auto max-h-[65vh] space-y-5">
              {/* Section 1: Informasi Dasar */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                  <Box className="w-4 h-4 text-purple-500" /> Informasi Dasar
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Kode Aset</label>
                    <input 
                      value={form.code}
                      onChange={(e) => setForm({...form, code: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="Kosongkan untuk auto"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Kategori <span className="text-red-500">*</span></label>
                    <select 
                      value={form.category}
                      onChange={(e) => setForm({...form, category: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nama Aset <span className="text-red-500">*</span></label>
                  <input 
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
                    placeholder="Contoh: OLT Huawei MA5608T"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Merk</label>
                    <input 
                      value={form.brand}
                      onChange={(e) => setForm({...form, brand: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="Huawei"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Model/Tipe</label>
                    <input 
                      value={form.model}
                      onChange={(e) => setForm({...form, model: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="MA5608T"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">No. Seri</label>
                    <input 
                      value={form.serial_number}
                      onChange={(e) => setForm({...form, serial_number: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="SN123456"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section 2: Informasi Pembelian */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-3">
                <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Informasi Pembelian
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tanggal Beli</label>
                    <input 
                      type="date"
                      value={form.purchase_date}
                      onChange={(e) => setForm({...form, purchase_date: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Harga Beli (Rp) <span className="text-red-500">*</span></label>
                    <input 
                      type="number"
                      value={form.purchase_price}
                      onChange={(e) => setForm({...form, purchase_price: parseFloat(e.target.value) || 0})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="5000000"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Supplier</label>
                    <input 
                      value={form.supplier}
                      onChange={(e) => setForm({...form, supplier: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="PT. Network Solution"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">No. Faktur</label>
                    <input 
                      value={form.invoice_number}
                      onChange={(e) => setForm({...form, invoice_number: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="INV-2024-001"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section 3: Lokasi & Penugasan */}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 space-y-3">
                <h4 className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Lokasi & Penugasan
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Lokasi Cabang</label>
                    <select
                      value={form.branch_id || ''}
                      onChange={(e) => setForm({...form, branch_id: e.target.value})}
                      disabled={isKepalaCabang}
                      data-testid="asset-branch-select"
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="">— Pilih Cabang —</option>
                      {visibleBranches.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Lokasi Detail</label>
                    <input 
                      value={form.location}
                      onChange={(e) => setForm({...form, location: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="NOC Room / Gudang / Rak A1"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ditugaskan ke</label>
                    <input 
                      value={form.assigned_to}
                      onChange={(e) => setForm({...form, assigned_to: e.target.value})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="Nama teknisi"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section 4: Penyusutan */}
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 space-y-3">
                <h4 className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" /> Penyusutan
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Umur Ekonomis (tahun)</label>
                    <input 
                      type="number"
                      value={form.useful_life_years}
                      onChange={(e) => setForm({...form, useful_life_years: parseInt(e.target.value) || 4})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-800"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nilai Residu (Rp)</label>
                    <input 
                      type="number"
                      value={form.salvage_value}
                      onChange={(e) => setForm({...form, salvage_value: parseFloat(e.target.value) || 0})}
                      className="h-9 w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-800"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
              
              {/* Catatan */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Catatan</label>
                <textarea 
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800"
                  rows={2}
                  placeholder="Catatan tambahan..."
                />
              </div>
              
              {/* Auto Journal Toggle */}
              <label className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                <input 
                  type="checkbox"
                  checked={form.auto_journal}
                  onChange={(e) => setForm({...form, auto_journal: e.target.checked})}
                  className="w-4 h-4 rounded text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Otomatis catat ke Akuntansi</span>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Buat jurnal pembelian aset secara otomatis</p>
                </div>
              </label>
            </div>
            
            <div className="px-3 py-2 border-t flex justify-end gap-2 bg-gray-50 dark:bg-slate-900">
              <button onClick={() => setShowAddModal(false)} className="px-3 py-1.5 text-xs border rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                Batal
              </button>
              <button 
                onClick={handleAddAsset}
                disabled={saving}
                className="px-3 py-1.5 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail/Edit Modal */}
      {showDetailModal && selectedAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="px-3 py-2 border-b flex justify-between items-center bg-blue-500 text-white">
              <h3 className="text-sm font-medium">📦 {selectedAsset.code} - {selectedAsset.name}</h3>
              <button onClick={() => { setShowDetailModal(false); setSelectedAsset(null); }} className="text-white/80 hover:text-white">×</button>
            </div>
            
            <div className="p-3 overflow-y-auto max-h-[70vh] space-y-3">
              {/* Asset Info */}
              <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded-lg space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-slate-400">Harga Beli:</span>
                  <span className="font-semibold">{formatCurrency(selectedAsset.purchase_price)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-slate-400">Akum. Penyusutan:</span>
                  <span className="font-semibold text-orange-600 dark:text-orange-400">{formatCurrency(selectedAsset.accumulated_depreciation)}</span>
                </div>
                <div className="flex justify-between text-xs border-t pt-1">
                  <span className="text-gray-500 dark:text-slate-400">Nilai Buku:</span>
                  <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(selectedAsset.book_value)}</span>
                </div>
              </div>
              
              {/* Editable Fields */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Cabang</label>
                  <select
                    value={form.branch_id || ''}
                    onChange={(e) => setForm({...form, branch_id: e.target.value})}
                    disabled={isKepalaCabang}
                    className="h-7 w-full text-xs border rounded px-2 bg-white disabled:opacity-60 disabled:cursor-not-allowed dark:bg-slate-800"
                  >
                    <option value="">— Pilih —</option>
                    {visibleBranches.map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Lokasi</label>
                  <input 
                    value={form.location}
                    onChange={(e) => setForm({...form, location: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Ditugaskan ke</label>
                  <input 
                    value={form.assigned_to}
                    onChange={(e) => setForm({...form, assigned_to: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Kondisi</label>
                  <select 
                    value={form.condition}
                    onChange={(e) => setForm({...form, condition: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  >
                    {conditions.map(cond => (
                      <option key={cond.value} value={cond.value}>{cond.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Status</label>
                  <select 
                    value={form.status}
                    onChange={(e) => setForm({...form, status: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  >
                    {statuses.map(stat => (
                      <option key={stat.value} value={stat.value}>{stat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Catatan</label>
                <textarea 
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="w-full text-xs border rounded px-2 py-1"
                  rows={2}
                />
              </div>
              
              {/* Transfer History */}
              {selectedAsset.transfer_history?.length > 0 && (
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 dark:text-slate-400">Riwayat Mutasi</label>
                  <div className="border rounded divide-y max-h-24 overflow-y-auto">
                    {selectedAsset.transfer_history.map((t, idx) => (
                      <div key={idx} className="px-2 py-1 text-[10px]">
                        <span className="text-gray-500 dark:text-slate-400">{new Date(t.transfer_date).toLocaleDateString('id-ID')}</span>
                        {t.from_location && t.to_location && (
                          <span className="ml-2">{t.from_location} → {t.to_location}</span>
                        )}
                        {t.from_user && t.to_user && (
                          <span className="ml-2">{t.from_user} → {t.to_user}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-3 py-2 border-t flex justify-end gap-2 bg-gray-50 dark:bg-slate-900">
              <button onClick={() => { setShowDetailModal(false); setSelectedAsset(null); }} className="px-3 py-1.5 text-xs border rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                Tutup
              </button>
              <button 
                onClick={handleUpdateAsset}
                disabled={saving}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Asset Modal */}
      {showEditModal && selectedAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="px-3 py-2 border-b flex justify-between items-center bg-green-500 text-white">
              <h3 className="text-sm font-medium">✏️ Edit Aset: {selectedAsset.code}</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedAsset(null); }} className="text-white/80 hover:text-white">×</button>
            </div>
            
            <div className="p-3 overflow-y-auto max-h-[70vh] space-y-3">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Kategori</label>
                  <select 
                    value={form.category}
                    onChange={(e) => setForm({...form, category: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Kondisi</label>
                  <select 
                    value={form.condition}
                    onChange={(e) => setForm({...form, condition: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  >
                    {conditions.map(cond => (
                      <option key={cond.value} value={cond.value}>{cond.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Nama Aset</label>
                <input 
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="h-7 w-full text-xs border rounded px-2"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Merk</label>
                  <input 
                    value={form.brand}
                    onChange={(e) => setForm({...form, brand: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Model/Tipe</label>
                  <input 
                    value={form.model}
                    onChange={(e) => setForm({...form, model: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">No. Seri</label>
                  <input 
                    value={form.serial_number}
                    onChange={(e) => setForm({...form, serial_number: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Cabang</label>
                  <select
                    value={form.branch_id || ''}
                    onChange={(e) => setForm({...form, branch_id: e.target.value})}
                    disabled={isKepalaCabang}
                    className="h-7 w-full text-xs border rounded px-2 bg-white disabled:opacity-60 disabled:cursor-not-allowed dark:bg-slate-800"
                  >
                    <option value="">— Pilih —</option>
                    {visibleBranches.map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Lokasi</label>
                  <input 
                    value={form.location}
                    onChange={(e) => setForm({...form, location: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Ditugaskan ke</label>
                  <input 
                    value={form.assigned_to}
                    onChange={(e) => setForm({...form, assigned_to: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
              </div>
              
              {/* Financial Info */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Harga Beli (Rp)</label>
                  <input 
                    type="number"
                    value={form.purchase_price}
                    onChange={(e) => setForm({...form, purchase_price: parseFloat(e.target.value) || 0})}
                    className="h-7 w-full text-xs border rounded px-2"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Nilai Residu (Rp)</label>
                  <input 
                    type="number"
                    value={form.salvage_value}
                    onChange={(e) => setForm({...form, salvage_value: parseFloat(e.target.value) || 0})}
                    className="h-7 w-full text-xs border rounded px-2"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Tanggal Beli</label>
                  <input 
                    type="date"
                    value={form.purchase_date}
                    onChange={(e) => setForm({...form, purchase_date: e.target.value})}
                    className="h-7 w-full text-xs border rounded px-2"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Umur Ekonomis (tahun)</label>
                  <input 
                    type="number"
                    value={form.useful_life_years}
                    onChange={(e) => setForm({...form, useful_life_years: parseInt(e.target.value) || 4})}
                    className="h-7 w-full text-xs border rounded px-2"
                    min="1"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Status</label>
                <select 
                  value={form.status}
                  onChange={(e) => setForm({...form, status: e.target.value})}
                  className="h-7 w-full text-xs border rounded px-2"
                >
                  {statuses.map(stat => (
                    <option key={stat.value} value={stat.value}>{stat.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5 dark:text-slate-400">Catatan</label>
                <textarea 
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="w-full text-xs border rounded px-2 py-1"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="px-3 py-2 border-t flex justify-end gap-2 bg-gray-50 dark:bg-slate-900">
              <button onClick={() => { setShowEditModal(false); setSelectedAsset(null); }} className="px-3 py-1.5 text-xs border rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                Batal
              </button>
              <button 
                onClick={() => {
                  handleUpdateAsset();
                  setShowEditModal(false);
                }}
                disabled={saving}
                className="px-3 py-1.5 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
              >
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDialog.show && confirmDialog.asset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-100 rounded-full dark:bg-red-900/40">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Hapus Aset</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Apakah Anda yakin ingin menghapus aset ini?
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-3">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{confirmDialog.asset.name}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">{confirmDialog.asset.code}</p>
              </div>
            </div>
            <div className="px-4 py-3 border-t bg-gray-50 dark:bg-gray-800 flex justify-end gap-2">
              <button 
                onClick={() => setConfirmDialog({ show: false, asset: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors dark:text-slate-200 dark:bg-slate-800 dark:border-slate-600 dark:hover:bg-slate-700/50"
              >
                Batal
              </button>
              <button 
                onClick={confirmDeleteAsset}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
              >
                Ya, Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
